import React, { useEffect, useContext, useMemo, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, pointerWithin } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

import SponsorsPanel from './sponsor/SponsorsPanel';
import PlacementsPanel from './placement/PlacementsPanel';
import { Context } from './contexts/OnboardingContext';
import SponsorProvider from './contexts/SponsorContext';
import PlacementProvider from './contexts/PlacementContext';

const PlacementsSponsors = () => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );
    const [activeId, setActiveId] = useState(null);

    const {
        isDraggableActive,
        setDraggableActive,
        currentSelectedPlacement,
        setIsDragging,
        currentSponsorsLists,
        setCurrentSponsorsList,
        sponsorsToAdd,
        setSponsorsToAdd,
        selectedSponsors,
        setSelectedSponsors,
        canAddPlacementSponsors,
    } = useContext(Context);

    useEffect(() => {
        if (!currentSelectedPlacement) {
            setSponsorsToAdd([]);
            setDraggableActive(false);
        } else if (
            currentSelectedPlacement &&
            Array.isArray(currentSelectedPlacement.sponsors)
        ) {
            const newSponsorList = currentSponsorsLists.reduce((acc, sp) => {
                if (
                    !sp ||
                    currentSelectedPlacement.sponsors.findIndex(
                        el => el && el.id === sp.id,
                    ) > -1
                )
                    return acc;
                acc.push(sp);
                return acc;
            }, []);
            setCurrentSponsorsList(newSponsorList);
        }
    }, [currentSelectedPlacement]);

    useEffect(() => {
        if (!isDraggableActive) return;
    }, [isDraggableActive]);

    useEffect(() => {
        return () => {
            if (typeof setSelectedSponsors === 'function') {
                setSelectedSponsors([]);
            }
        };
    }, []);

    const onDragStart = event => {
        setIsDragging(true);
        setActiveId(event?.active?.id || null);
    };

    const onDragEnd = event => {
        setIsDragging(false);
        const { over, active } = event;
        setActiveId(null);
        if (!canAddPlacementSponsors) return;
        if (!over) return;
        if (over.id !== 'droppable_placements') return;
        const sp = currentSponsorsLists.find(s => `${s.id}` === `${active.id}`);
        if (!sp) return;
        const selectedSet = Array.isArray(selectedSponsors) ? selectedSponsors : [];
        const baseList = selectedSet.length > 0
            ? (selectedSet.find(ss => ss.id === sp.id) ? selectedSet : [...selectedSet, sp])
            : [sp];
        const seen = new Set();
        const dropList = baseList.filter(item => {
            const key = `${item.id}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        setSponsorsToAdd([
            ...sponsorsToAdd,
            ...dropList.map(item => ({ ...item, isNew: true, toDelete: false })),
        ]);
        const toRemove = new Set(dropList.map(item => item.id));
        setCurrentSponsorsList(currentSponsorsLists.filter(s => s && !toRemove.has(s.id)));
        if (typeof setSelectedSponsors === 'function') {
            setSelectedSponsors([]);
        }
    };

    const activeSponsor = useMemo(() => {
        if (!activeId) return null;
        return currentSponsorsLists.find(s => `${s.id}` === `${activeId}`) || null;
    }, [activeId, currentSponsorsLists]);

    return (
        <div className="gds-flex-grid__container -m-t-2">
            <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div className="gds-flex-grid__row">
                    <PlacementProvider>
                        <PlacementsPanel />
                    </PlacementProvider>

                    <SponsorProvider>
                        <SponsorsPanel />
                    </SponsorProvider>
                </div>
                <DragOverlay modifiers={[snapCenterToCursor]}>
                    {activeSponsor ? (() => {
                        const selCount = Array.isArray(selectedSponsors) ? selectedSponsors.length : 0;
                        const includesActive = selCount > 0 && selectedSponsors.some(s => `${s.id}` === `${activeId}`);
                        const count = selCount > 0 ? (includesActive ? selCount : selCount + 1) : 1;

                        const renderSponsorCard = () => (
                            <div className="gds-card">
                                <div className="gds-flex gds-flex--align-center gds-text--body-sm -p-a-2">
                                    <div className="gds-sponsors__image -m-l-1 -m-r-2 -color-bg-white -cursor--pointer">
                                        {activeSponsor.logos && activeSponsor.logos.length > 0 ? (
                                            <img src={activeSponsor.logos[0].url} height="100%" width="100%" />
                                        ) : (
                                            <div className="gds-flex gds-flex--justify-center gds-flex--content-center gds-flex-grid__item--full-height">
                                                <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="gds-sponsor__name">
                                        <p className="gds-text--body-sm gds-text--regular -ellipsis -p-r-1">
                                            {activeSponsor.name}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );

                        if (count > 1) {
                            return (
                                <div
                                    className="gds-sponsor gds-sponsor--stacked"
                                    style={{ position: 'relative', zIndex: 9999, pointerEvents: 'none', width: '50%' }}
                                >
                                    <div className="gds-sponsor gds-sponsor--bottom">
                                        {renderSponsorCard()}
                                    </div>
                                    <div className="gds-sponsor gds-sponsor--top">
                                        <span className="-abs-t-r gds-number-circle gds-number-circle--xs -m-r-0 -color-bg-pri -color-tx-white -z-1 -pointer-events--none">{count}</span>
                                        {renderSponsorCard()}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div
                                className="gds-sponsor"
                                style={{ position: 'relative', zIndex: 9999, pointerEvents: 'none', width: '50%' }}
                            >
                                {renderSponsorCard()}
                            </div>
                        );
                    })() : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
};

export default PlacementsSponsors;
